import { useCallback, useEffect, useState } from 'react'

const HIDDEN_KEY = 'nvs.dashboard.hidden.v1'
const SETTINGS_KEY = 'nvs.dashboard.settings.v1'

function readHidden(): string[] {
  try {
    const raw = globalThis.localStorage?.getItem(HIDDEN_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

function readSettings(): Record<string, unknown> {
  try {
    const raw = globalThis.localStorage?.getItem(SETTINGS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/**
 * Estado de personalização do dashboard, persistido em localStorage.
 * - `hidden`: IDs de widgets escondidos. Default "tudo visível" garante que
 *   widgets novos apareçam automaticamente quando forem lançados.
 * - `settings`: configuração arbitrária por widget (ex.: tamanho da lista).
 *   Cada widget escolhe sua própria forma — o hook só armazena/recupera JSON.
 */
export function useDashboardLayout() {
  const [hidden, setHidden] = useState<string[]>(() => readHidden())
  const [settings, setSettings] = useState<Record<string, unknown>>(() => readSettings())

  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(HIDDEN_KEY, JSON.stringify(hidden))
    } catch {
      /* ignore */
    }
  }, [hidden])

  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(SETTINGS_KEY, JSON.stringify(settings))
    } catch {
      /* ignore */
    }
  }, [settings])

  const isHidden = useCallback((id: string) => hidden.includes(id), [hidden])

  const toggle = useCallback((id: string) => {
    setHidden((prev) => (prev.includes(id) ? prev.filter((h) => h !== id) : [...prev, id]))
  }, [])

  const reset = useCallback(() => {
    setHidden([])
    setSettings({})
  }, [])

  const getSetting = useCallback(
    <T,>(id: string, fallback: T): T => {
      const v = settings[id]
      return v === undefined ? fallback : (v as T)
    },
    [settings],
  )

  const setSetting = useCallback(<T,>(id: string, value: T) => {
    setSettings((prev) => ({ ...prev, [id]: value }))
  }, [])

  return {
    hidden,
    isHidden,
    toggle,
    reset,
    hiddenCount: hidden.length,
    getSetting,
    setSetting,
  }
}
